import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadImage, isShareSupported } from '../common/shareService';
import '../css/TimelapseDialog.css';

const TimelapseDialog = ({
    open,
    mode = 'create',
    frameCount,
    isGenerating,
    generateProgress,
    generateError,
    videoBlob,
    videoMimeType,
    onGenerate,
    onRegenerate,
    onSave,
    onSkip,
    onClose
}) => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const [fps, setFps] = useState(10);
    const [canShare, setCanShare] = useState(false);
    const [showRegenOptions, setShowRegenOptions] = useState(false);

    useEffect(() => {
        setCanShare(isShareSupported());
    }, []);

    useEffect(() => {
        if (videoBlob && videoRef.current) {
            const url = URL.createObjectURL(videoBlob);
            videoRef.current.src = url;
            return () => URL.revokeObjectURL(url);
        }
    }, [videoBlob]);

    if (!open) return null;

    const ext = videoMimeType && videoMimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
    const blobType = ext === 'mp4' ? 'video/mp4' : 'video/webm';

    const handleDownload = () => {
        if (videoBlob) {
            downloadImage(videoBlob, `study-timelapse-${Date.now()}.${ext}`);
        }
    };

    const handleShare = async () => {
        if (!videoBlob) return;
        try {
            const file = new File([videoBlob], `study-timelapse.${ext}`, { type: blobType });
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: t('timelapse.title')
                });
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Share failed:', error);
            }
        }
    };

    // MediaRecorder 미지원
    const isSupported = window.MediaRecorder &&
        (MediaRecorder.isTypeSupported('video/mp4') || MediaRecorder.isTypeSupported('video/webm'));

    // playback 모드: 비디오만 재생
    if (mode === 'playback') {
        return (
            <div className="timelapse-overlay" onClick={onClose}>
                <div className="timelapse-dialog" onClick={(e) => e.stopPropagation()}>
                    <h3 className="timelapse-title">{t('timelapse.title')}</h3>
                    <div className="timelapse-complete">
                        <video
                            ref={videoRef}
                            className="timelapse-preview"
                            controls
                            autoPlay
                            loop
                            playsInline
                            muted
                        />
                        <div className="timelapse-actions">
                            <button className="timelapse-btn timelapse-btn-download" onClick={handleDownload}>
                                {t('timelapse.download')}
                            </button>
                            {canShare && (
                                <button className="timelapse-btn timelapse-btn-share" onClick={handleShare}>
                                    {t('share.share')}
                                </button>
                            )}
                            <button className="timelapse-btn timelapse-btn-close" onClick={onClose}>
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // generate-from-history 모드: fps 선택 → 생성 → 미리보기
    if (mode === 'generate-from-history') {
        return (
            <div className="timelapse-overlay" onClick={onClose}>
                <div className="timelapse-dialog" onClick={(e) => e.stopPropagation()}>
                    <h3 className="timelapse-title">{t('timelapse.title')}</h3>
                    {!isSupported ? (
                        <div className="timelapse-unsupported">
                            <p>{t('timelapse.unsupported')}</p>
                            <button className="timelapse-btn timelapse-btn-close" onClick={onClose}>
                                {t('close')}
                            </button>
                        </div>
                    ) : generateError ? (
                        <div className="timelapse-error">
                            <p>{t('timelapse.error') || 'Generation failed'}</p>
                            <div className="timelapse-actions">
                                <button className="timelapse-btn timelapse-btn-generate" onClick={() => onGenerate(fps)}>
                                    {t('timelapse.retry') || 'Retry'}
                                </button>
                                <button className="timelapse-btn timelapse-btn-close" onClick={onClose}>
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    ) : isGenerating ? (
                        <div className="timelapse-generating">
                            <p>{t('timelapse.generating')}</p>
                            <div className="timelapse-progress-bar">
                                <div className="timelapse-progress-fill" style={{ width: `${generateProgress}%` }} />
                            </div>
                            <p className="timelapse-progress-text">{generateProgress}%</p>
                        </div>
                    ) : videoBlob ? (
                        <div className="timelapse-complete">
                            <p className="timelapse-complete-text">{t('timelapse.complete')}</p>
                            <video ref={videoRef} className="timelapse-preview" controls autoPlay loop playsInline muted />
                            <div className="timelapse-actions">
                                <button className="timelapse-btn timelapse-btn-download" onClick={handleDownload}>
                                    {t('timelapse.download')}
                                </button>
                                {canShare && (
                                    <button className="timelapse-btn timelapse-btn-share" onClick={handleShare}>
                                        {t('share.share')}
                                    </button>
                                )}
                                <button className="timelapse-btn timelapse-btn-close" onClick={onClose}>
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="timelapse-ready">
                            <p className="timelapse-frame-info">
                                {t('timelapse.frames', { count: frameCount })}
                            </p>
                            <div className="timelapse-fps-selector">
                                <label>{t('timelapse.speed')}</label>
                                <div className="timelapse-fps-options">
                                    {[5, 10, 15].map(v => (
                                        <button key={v} className={`timelapse-fps-btn ${fps === v ? 'active' : ''}`} onClick={() => setFps(v)}>
                                            {v}fps
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="timelapse-actions">
                                <button className="timelapse-btn timelapse-btn-generate" onClick={() => onGenerate(fps)}>
                                    {t('timelapse.generate')}
                                </button>
                                <button className="timelapse-btn timelapse-btn-close" onClick={onClose}>
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // create 모드 (기본, 방 닫기 시)
    return (
        <div className="timelapse-overlay" onClick={onSkip}>
            <div className="timelapse-dialog" onClick={(e) => e.stopPropagation()}>
                <h3 className="timelapse-title">{t('timelapse.title')}</h3>

                {!isSupported ? (
                    <div className="timelapse-unsupported">
                        <p>{t('timelapse.unsupported')}</p>
                        <button className="timelapse-btn timelapse-btn-skip" onClick={onSkip}>
                            {t('timelapse.skip')}
                        </button>
                    </div>
                ) : generateError ? (
                    <div className="timelapse-error">
                        <p>{t('timelapse.error') || 'Generation failed'}</p>
                        <div className="timelapse-actions">
                            <button className="timelapse-btn timelapse-btn-generate" onClick={() => onGenerate(fps)}>
                                {t('timelapse.retry') || 'Retry'}
                            </button>
                            <button className="timelapse-btn timelapse-btn-skip" onClick={onSkip}>
                                {t('timelapse.skip')}
                            </button>
                        </div>
                    </div>
                ) : !videoBlob && !isGenerating ? (
                    // 생성 전
                    <div className="timelapse-ready">
                        <p className="timelapse-frame-info">
                            {t('timelapse.frames', { count: frameCount })}
                        </p>

                        <div className="timelapse-fps-selector">
                            <label>{t('timelapse.speed')}</label>
                            <div className="timelapse-fps-options">
                                {[5, 10, 15].map(v => (
                                    <button
                                        key={v}
                                        className={`timelapse-fps-btn ${fps === v ? 'active' : ''}`}
                                        onClick={() => setFps(v)}
                                    >
                                        {v}fps
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="timelapse-actions">
                            <button
                                className="timelapse-btn timelapse-btn-generate"
                                onClick={() => onGenerate(fps)}
                            >
                                {t('timelapse.generate')}
                            </button>
                            <button className="timelapse-btn timelapse-btn-skip" onClick={onSkip}>
                                {t('timelapse.skip')}
                            </button>
                        </div>
                    </div>
                ) : isGenerating ? (
                    // 생성 중
                    <div className="timelapse-generating">
                        <p>{t('timelapse.generating')}</p>
                        <div className="timelapse-progress-bar">
                            <div
                                className="timelapse-progress-fill"
                                style={{ width: `${generateProgress}%` }}
                            />
                        </div>
                        <p className="timelapse-progress-text">{generateProgress}%</p>
                    </div>
                ) : (
                    // 완료
                    <div className="timelapse-complete">
                        <p className="timelapse-complete-text">{t('timelapse.complete')}</p>
                        <video
                            ref={videoRef}
                            className="timelapse-preview"
                            controls
                            autoPlay
                            loop
                            playsInline
                            muted
                        />

                        {showRegenOptions && (
                            <div className="timelapse-regen-options">
                                <div className="timelapse-fps-selector">
                                    <label>{t('timelapse.speed')}</label>
                                    <div className="timelapse-fps-options">
                                        {[5, 10, 15].map(v => (
                                            <button
                                                key={v}
                                                className={`timelapse-fps-btn ${fps === v ? 'active' : ''}`}
                                                onClick={() => setFps(v)}
                                            >
                                                {v}fps
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    className="timelapse-btn timelapse-btn-generate"
                                    onClick={() => {
                                        setShowRegenOptions(false);
                                        onRegenerate(fps);
                                    }}
                                >
                                    {t('timelapse.generate')}
                                </button>
                            </div>
                        )}

                        <div className="timelapse-actions">
                            <button
                                className="timelapse-btn timelapse-btn-download"
                                onClick={handleDownload}
                            >
                                {t('timelapse.download')}
                            </button>
                            {canShare && (
                                <button
                                    className="timelapse-btn timelapse-btn-share"
                                    onClick={handleShare}
                                >
                                    {t('share.share')}
                                </button>
                            )}
                            {!showRegenOptions && (
                                <button
                                    className="timelapse-btn timelapse-btn-regenerate"
                                    onClick={() => setShowRegenOptions(true)}
                                >
                                    {t('timelapse.regenerate')}
                                </button>
                            )}
                            <button className="timelapse-btn timelapse-btn-save" onClick={onSave}>
                                {t('timelapse.save')}
                            </button>
                            <button className="timelapse-btn timelapse-btn-skip" onClick={onSkip}>
                                {t('timelapse.discard')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TimelapseDialog;
